import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { isTelegramWebApp, getTelegramData, initTelegramWebApp, showTelegramAlert, applyTelegramTheme } from '../utils/telegram';
import { validateTelegramData, getShopInfo, scanPrize } from '../services/api';
import QRScanner from './QRScanner';
import StatusModal from './StatusModal';

const ShopApp = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [shopInfo, setShopInfo] = useState(null);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const [telegramData, setTelegramData] = useState(null);
  const [theme, setTheme] = useState('light');
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');

  // Собираем debug-инфо о состоянии Telegram SDK
  const collectDebugInfo = () => {
    try {
      return {
        tg: typeof window.Telegram,
        tgWA: typeof window.Telegram?.WebApp,
        version: window.Telegram?.WebApp?.version || 'N/A',
        platform: window.Telegram?.WebApp?.platform || 'N/A',
        hasInitData: !!window.Telegram?.WebApp?.initData,
        initDataLen: window.Telegram?.WebApp?.initData?.length || 0,
        ua: navigator.userAgent.slice(0, 80),
      };
    } catch (e) {
      return { error: String(e) };
    }
  };

  // Ждём загрузки Telegram SDK (до 3 секунд)
  const waitForTelegramSDK = () => new Promise((resolve) => {
    if (window.Telegram?.WebApp) return resolve(true);
    let attempts = 0;
    const interval = setInterval(() => {
      attempts++;
      if (window.Telegram?.WebApp || attempts >= 30) {
        clearInterval(interval);
        resolve(!!window.Telegram?.WebApp);
      }
    }, 100);
  });

  useEffect(() => {
    initializeApp();
  }, []);

  const initializeApp = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Применяем тему Telegram
      const themeData = applyTelegramTheme();
      setTheme(themeData.theme);

      // Проверяем, запущено ли приложение в Telegram (ждём загрузки SDK)
      const sdkLoaded = await waitForTelegramSDK();
      if (!sdkLoaded || !isTelegramWebApp()) {
        setDebugInfo(collectDebugInfo());
        setError('Это приложение должно быть запущено в Telegram');
        setIsLoading(false);
        return;
      }

      // Инициализируем Telegram WebApp
      initTelegramWebApp();

      // Получаем данные из Telegram
      const data = getTelegramData();
      if (!data) {
        setError('Откройте приложение через кнопку в боте @MdaPrize_Bot');
        setIsLoading(false);
        return;
      }

      setTelegramData(data);

      // Валидируем данные на сервере
      const validationResult = await validateTelegramData(data);

      if (!validationResult.success) {
        setError(validationResult.error || 'Данные не прошли валидацию');
        setIsLoading(false);
        return;
      }

      // Получаем информацию о магазине
      const shopData = await getShopInfo(validationResult.chatId);

      if (!shopData.success) {
        setError(shopData.error || 'Не удалось получить информацию о магазине');
        setIsLoading(false);
        return;
      }

      setShopInfo(shopData.data);

      setIsValid(true);
      setIsLoading(false);

    } catch (error) {
      console.error('App initialization error:', error);
      setDebugInfo(collectDebugInfo());
      setError('Ошибка при инициализации приложения');
      setIsLoading(false);
    }
  };

  const handleQRScanSuccess = async (rawQrData) => {
    try {
      // Код могут ввести вручную строчными буквами / с пробелами
      const qrData = String(rawQrData || '').trim().replace(/^p-/i, 'P-');

      if (!telegramData) {
        showTelegramAlert('❌ Ошибка: данные Telegram не загружены');
        return;
      }

      // Показываем модальное окно статуса
      setStatusMessage('🔍 Проверяю QR код...');
      setIsStatusModalOpen(true);

      // Проверяем, что это не URL
      if (qrData.startsWith('http://') || qrData.startsWith('https://')) {
        setStatusMessage('❌ Обнаружен URL вместо QR кода\n\n💡 Пожалуйста, отсканируйте QR код приза');

        // Закрываем QR сканер Telegram WebApp
        if (window.Telegram?.WebApp?.closeScanQrPopup) {
          window.Telegram.WebApp.closeScanQrPopup();
        }
        return;
      }

      // Проверяем формат QR кода (поддерживаем старый и новый форматы)
      const oldFormatValid = qrData.startsWith('PRIZE:') && qrData.split(':').length === 3;
      const newFormatValid = /^P-[A-Z0-9]{8}-[0-9A-F]{2}$/.test(qrData);
      // Голый короткий код — его показывают клиенту в приложении
      const shortCodeValid = /^[A-Z0-9]{8}$/.test(qrData);

      if (!oldFormatValid && !newFormatValid && !shortCodeValid) {
        setStatusMessage('❌ Неверный формат кода\n\n💡 Ожидается код приза (8 символов) или QR формата P-XXXXXXXX-XX');

        // Закрываем QR сканер Telegram WebApp
        if (window.Telegram?.WebApp?.closeScanQrPopup) {
          window.Telegram.WebApp.closeScanQrPopup();
        }
        return;
      }

      const chatId = telegramData.chat?.id || telegramData.user?.id;

      // Отправляем запрос на сервер для обработки QR кода
      const result = await scanPrize(qrData, chatId);

      if (result.success) {
        // Проверяем, что данные приза не пустые
        if (!result.data || !result.data.prize || Object.keys(result.data.prize).length === 0 || !result.data.prize.name) {
          setStatusMessage('❌ QR код не найден\n\n🔄 Попробуйте еще раз или напишите @sysrootix');

          // Закрываем QR сканер Telegram WebApp
          if (window.Telegram?.WebApp?.closeScanQrPopup) {
            window.Telegram.WebApp.closeScanQrPopup();
          }
          return;
        }

        // Закрываем модальное окно статуса и показываем успешное сообщение
        setIsStatusModalOpen(false);

        // Закрываем QR сканер Telegram WebApp
        if (window.Telegram?.WebApp?.closeScanQrPopup) {
          window.Telegram.WebApp.closeScanQrPopup();
        }

        // Показываем краткое уведомление об успешном сканировании
        showTelegramAlert('✅ QR код успешно обработан!');

        // Переходим на страницу приза с передачей данных через state
        // Отфильтровываем функции из telegramData, так как они не могут быть сериализованы
        const cleanTelegramData = {
          initData: telegramData.initData,
          initDataUnsafe: telegramData.initDataUnsafe,
          user: telegramData.user,
          chat: telegramData.chat,
          chatType: telegramData.chatType,
          startParam: telegramData.startParam,
          canSendAfter: telegramData.canSendAfter,
          authDate: telegramData.authDate,
          hash: telegramData.hash,
          queryId: telegramData.queryId,
          receiver: telegramData.receiver,
          chatInstance: telegramData.chatInstance,
          isExpired: telegramData.isExpired,
          isFromSwitch: telegramData.isFromSwitch,
          platform: telegramData.platform,
          colorScheme: telegramData.colorScheme,
          themeParams: telegramData.themeParams,
          headerColor: telegramData.headerColor,
          backgroundColor: telegramData.backgroundColor,
          isClosingConfirmationEnabled: telegramData.isClosingConfirmationEnabled
        };

        navigate('/prize', {
          state: {
            prizeData: result.data,
            shopInfo: shopInfo,
            telegramData: cleanTelegramData
          }
        });
      } else {
        // Определяем тип ошибки от сервера
        let errorMessage = '❌ QR код не найден\n\n🔄 Попробуйте еще раз или напишите @sysrootix';

        if (result.error && result.error.includes('400')) {
          errorMessage = '❌ QR код не найден\n\n🔄 Попробуйте еще раз или напишите @sysrootix';
        } else if (result.error && result.error.includes('404')) {
          errorMessage = '❌ QR код не найден\n\n🔄 Попробуйте еще раз или напишите @sysrootix';
        } else if (result.error && result.error.includes('500')) {
          errorMessage = '❌ Ошибка сервера\n\n🔄 Попробуйте позже или напишите @sysrootix';
        } else if (result.error && result.error.includes('unauthorized')) {
          errorMessage = '❌ Ошибка авторизации\n\n🔄 Попробуйте еще раз или напишите @sysrootix';
        }

        // Закрываем модальное окно статуса и закрываем QR сканер
        setIsStatusModalOpen(false);

        // Закрываем QR сканер Telegram WebApp
        if (window.Telegram?.WebApp?.closeScanQrPopup) {
          window.Telegram.WebApp.closeScanQrPopup();
        }

        showTelegramAlert(errorMessage);
      }
    } catch (error) {
      console.error('❌ Error processing QR code:', error);
      console.error('❌ Error stack:', error.stack);
      console.error('❌ Error name:', error.name);

      // Определяем тип ошибки и показываем соответствующее сообщение
      let errorMessage = '❌ Ошибка при обработке QR кода\n\n🔄 Попробуйте еще раз или напишите @sysrootix';

      if (error.message.includes('400')) {
        errorMessage = '❌ QR код не найден\n\n🔄 Попробуйте еще раз или напишите @sysrootix';
      } else if (error.message.includes('404')) {
        errorMessage = '❌ QR код не найден\n\n🔄 Попробуйте еще раз или напишите @sysrootix';
      } else if (error.message.includes('500')) {
        errorMessage = '❌ Ошибка сервера\n\n🔄 Попробуйте позже или напишите @sysrootix';
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        errorMessage = '❌ Ошибка соединения\n\n🔄 Проверьте интернет и попробуйте еще раз';
      }

      // Закрываем модальное окно статуса и закрываем QR сканер
      setIsStatusModalOpen(false);

      // Закрываем QR сканер Telegram WebApp
      if (window.Telegram?.WebApp?.closeScanQrPopup) {
        window.Telegram.WebApp.closeScanQrPopup();
      }

      showTelegramAlert(errorMessage);
    }
  };

  const handleQRScanError = (errorMessage) => {
    console.error('QR scan error:', errorMessage);

    // Закрываем QR сканер Telegram WebApp
    if (window.Telegram?.WebApp?.closeScanQrPopup) {
      window.Telegram.WebApp.closeScanQrPopup();
    }

    showTelegramAlert(`❌ Ошибка при сканировании: ${errorMessage}`);
  };

  if (isLoading) {
    return (
      <div className={`min-h-screen tg-gradient-${theme} flex items-center justify-center animate-fade-in safe-area-top safe-area-bottom px-4`}>
        <div className="text-center animate-scale-in">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-current mx-auto mb-4 text-tg-text"></div>
          <p className="text-tg-hint text-sm">Загрузка приложения...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen tg-gradient-${theme} flex items-center justify-center animate-fade-in safe-area-top safe-area-bottom px-4`}>
        <div className="max-w-sm w-full tg-card text-center animate-scale-in">
          <div className="text-red-500 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-tg-text mb-2">Ошибка</h2>
          <p className="text-tg-hint text-sm mb-4">{error}</p>

          {/* DEBUG INFO — показываем для диагностики */}
          {debugInfo && (
            <div style={{
              background: '#1a1a2e',
              border: '1px solid #444',
              borderRadius: '8px',
              padding: '10px',
              marginBottom: '12px',
              textAlign: 'left',
              fontSize: '11px',
              fontFamily: 'monospace',
              color: '#aef',
              wordBreak: 'break-all'
            }}>
              <div style={{ color: '#ff9', marginBottom: '4px', fontWeight: 'bold' }}>🔍 Debug info:</div>
              {Object.entries(debugInfo).map(([k, v]) => (
                <div key={k}><span style={{ color: '#f9a' }}>{k}:</span> {String(v)}</div>
              ))}
            </div>
          )}

          <button
            onClick={initializeApp}
            className="tg-button w-full"
          >
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  if (!isValid || !shopInfo) {
    return (
      <div className={`min-h-screen tg-gradient-${theme} flex items-center justify-center animate-fade-in safe-area-top safe-area-bottom px-4`}>
        <div className="max-w-sm w-full tg-card text-center animate-scale-in">
          <div className="text-yellow-500 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-tg-text mb-2">Доступ запрещен</h2>
          <p className="text-tg-hint text-sm">У вас нет доступа к этому приложению</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen tg-gradient-${theme} animate-fade-in safe-area-top safe-area-bottom`}>
      <div className="container mx-auto px-4 py-6 max-w-sm">
        {/* Header */}
        <div className="text-center mb-6 animate-slide-up">
          <div className="w-16 h-16 bg-tg-secondary-bg border border-tg-hint rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-soft">
            <svg className="w-8 h-8 text-tg-text" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-tg-text mb-1">
            {shopInfo.shop_name}
          </h1>
          <p className="text-tg-hint text-sm">
            Система выдачи призов
          </p>
        </div>


        {/* QR Scanner */}
        <div className="tg-card animate-slide-up mb-6" style={{ animationDelay: '0.2s' }}>
          <h2 className="text-lg font-semibold text-tg-text mb-3 text-center">
            Сканирование призов
          </h2>
          <QRScanner
            onScanSuccess={handleQRScanSuccess}
            onScanError={handleQRScanError}
          />
        </div>

        {/* Footer */}
        <div className="text-center animate-slide-up" style={{ animationDelay: '0.3s' }}>
          <p className="text-tg-hint text-xs">
            © 2025 Medusa Shop Panel
          </p>
        </div>
      </div>

      {/* Status Modal */}
      <StatusModal
        isOpen={isStatusModalOpen}
        message={statusMessage}
        isProcessing={isStatusModalOpen && !statusMessage.includes('❌')}
        onClose={() => setIsStatusModalOpen(false)}
      />
    </div>
  );
};

export default ShopApp; 